import { Typography } from '@euler/app/components/typography';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { MaybeText } from '@euler/components/MaybeText';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CommonTaskStatus } from '@euler/model/enum';
import { MotiView } from 'moti';
import { memo, ReactNode } from 'react';
import {
  StyleProp,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const ProgressCardInfoCell = memo(
  ({
    label,
    value,
    style,
    valueTextStyle,
  }: {
    label: string;
    value: string | ReactNode;
    style?: StyleProp<ViewStyle>;
    valueTextStyle?: StyleProp<TextStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <HStack justifyContent="flex-start" style={style}>
        <Typography.Label
          regular
          size={rpx(28)}
          color={theme.task.detail.card.infoLabelColor}
        >
          {`${label}: `}
        </Typography.Label>
        <MaybeText
          text={value}
          css={`
            font-family: ${FontFamily.NotoSans.Medium};
            color: ${theme.task.detail.card.infoValueColor};
            font-size: ${rpx(28)}px;
            margin-left: ${rpx(8)}px;
            margin-top: ${rpx(8)}px;
            margin-bottom: ${rpx(8)}px;
          `}
          style={valueTextStyle}
        />
      </HStack>
    );
  },
);

export const ProgressCardView = memo(
  ({
    title,
    style,
    index,
    children,
    status,
    statusButtonInProgressText,
    onPress,
  }: {
    title: string;
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
    index: number;
    status: CommonTaskStatus;
    statusButtonInProgressText?: string;
    onPress?: () => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const buttonBgColor =
      status === CommonTaskStatus.Pending
        ? theme.task.detail.card.status.bg.pending
        : status === CommonTaskStatus.InProgress
        ? theme.task.detail.card.status.bg.inprogress
        : theme.task.detail.card.status.bg.finished;
    const buttonTextColor =
      status === CommonTaskStatus.Pending
        ? theme.task.detail.card.status.fg.pending
        : status === CommonTaskStatus.InProgress
        ? theme.task.detail.card.status.fg.inprogress
        : theme.task.detail.card.status.fg.finished;
    const buttonText =
      status === CommonTaskStatus.Pending
        ? '未开始'
        : status === CommonTaskStatus.InProgress
        ? statusButtonInProgressText ?? '进行中'
        : '已完成';
    return (
      <MotiView
        from={{
          opacity: 0,
          translateX: rpx(50),
        }}
        animate={{ opacity: 1, translateX: 0 }}
        delay={index * 50}
        css={`
          background-color: ${theme.task.detail.card.bg};
          margin-right: ${rpx(40)}px;
          border-radius: ${rpx(20)}px;
        `}
        style={style}
      >
        <TouchableOpacity
          css={`
            flex: 1;
          `}
          onPress={onPress}
        >
          <Typography.Label
            black
            size={rpx(40)}
            color={theme.task.detail.card.titleTextColor}
            css={`
              text-align: center;
              margin: ${rpx(32)}px;
            `}
          >
            {title}
          </Typography.Label>
          <View
            css={`
              height: 1px;
              background-color: ${theme.task.detail.card.titleSeparatorColor};
            `}
          />
          <View
            css={`
              justify-content: center;
              flex: 1;
              align-self: center;
            `}
          >
            {children}
          </View>
          <TouchableOpacity
            onPress={onPress}
            css={`
              align-self: center;
              width: ${rpx(316)}px;
              height: ${rpx(72)}px;
              border-radius: ${rpx(12)}px;
              background-color: ${buttonBgColor};
              flex-direction: row;
              align-items: center;
              justify-content: center;
              margin-bottom: ${rpx(56)}px;
            `}
          >
            <Typography.Label regular color={buttonTextColor} size={rpx(30)}>
              {buttonText}
            </Typography.Label>
            <Icon
              name="chevron-right"
              size={rpx(30)}
              color={buttonTextColor}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </MotiView>
    );
  },
);
